import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  from,
  mergeMap,
  Observable,
} from 'rxjs';
import {
  IGenericServiceResponse,
  IServiceResponse,
} from '../interfaces/IServiceResponse';
import { environment } from 'src/environments/environment';
import { EpisodeOfCareService } from './episodeOfCare.service';
import { PatientService } from './patient.service';
import { TranslateService } from '@ngx-translate/core';
import { SwalToastService } from './swal.service';
import { ICarePlanFileDropdown } from '../interfaces/CarePlanFile/ICarePlanFileDropdown';
import { saveAs } from 'file-saver';
import { IAddCarePlanFile } from '../interfaces/CarePlanFile/IAddCarePlanFile';
import { ALLOWED_MIME, validateUpload } from '../guards/file.guard';
import { ICarePlanFileDetailed } from '../interfaces/CarePlanFile/ICarePlanFileDetailed';

@Injectable({ providedIn: 'root' })
export class CarePlanFileService {
  public carePlanFileModalSub = new BehaviorSubject<boolean>(false);

  public carePlanFileEditSub = new BehaviorSubject<
    ICarePlanFileDetailed | undefined
  >(undefined);

  public carePlanFileDataSub = new BehaviorSubject<ICarePlanFileDetailed[]>([]);

  public carePlanFileLoadingSub = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly _http: HttpClient,
    private readonly eocService: EpisodeOfCareService,
    private readonly patientService: PatientService,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService
  ) {}

  async getData() {
    this.carePlanFileLoadingSub.next(true);
    this.getCarePlanFile(
      this.patientService.patientIDsignal(),
      this.eocService.currentVisitId()
    ).subscribe({
      next: (response) => {
        this.carePlanFileDataSub.next(response?.data!);
        this.carePlanFileLoadingSub.next(false);
      },
      error: (err) => {
        this.swalToastService.toastError('Unable to retrieve care plans');
        console.error('Error fetching care plans:', err);
      },
    });
  }

  getCarePlanFile(
    patientId: string,
    visitId?: string
  ): Observable<IGenericServiceResponse<ICarePlanFileDetailed[]>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    if (visitId) {
      return this._http.get<IGenericServiceResponse<ICarePlanFileDetailed[]>>(
        `${environment.BACKEND_URL}CarePlanFile/${patientId}?visitId=${visitId}`,
        {
          headers: headers,
        }
      );
    } else {
      return this._http.get<IGenericServiceResponse<ICarePlanFileDetailed[]>>(
        `${environment.BACKEND_URL}CarePlanFile/${patientId}`,
        {
          headers: headers,
        }
      );
    }
  }

  downloadPDF(item: ICarePlanFileDetailed): void {
    this._http
      .get(
        `${
          environment.BACKEND_URL
        }CarePlanFile/DownloadPDF/${item?.id?.toString()!}`,
        { responseType: 'blob' }
      )
      .subscribe({
        next: (blob) => {
          if (!ALLOWED_MIME.includes(blob.type as any)) {
            this.swalToastService.toastError('Unsupported file type.');
            return;
          }
          // prefer server-sent filename; fallback to path
          const path = item.path || '';
          const nameFromPath =
            path.substring(path.lastIndexOf('/') + 1) || item?.examTitle;
          const file = new Blob([blob], { type: blob.type });
          saveAs(file, nameFromPath);
        },
        error: () => {
          this.swalToastService.toastError('Unable to download the document');
        },
      });
  }

  getCarePlanFileDropdown(): Observable<
    IGenericServiceResponse<ICarePlanFileDropdown>
  > {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this._http.get<IGenericServiceResponse<ICarePlanFileDropdown>>(
      `${environment.BACKEND_URL}CarePlanFile/DropdownData`,
      {
        headers: headers,
      }
    );
  }

  getPDF(carePlanFileId: string): Observable<Blob> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this._http.get(
      `${environment.BACKEND_URL}CarePlanFile/DownloadPDF/${carePlanFileId}`,
      {
        headers: headers,
        responseType: 'blob',
      }
    );
  }

  addCarePlanFile(carePlanFileObj: IAddCarePlanFile): Observable<any> {
    carePlanFileObj.visitId = this.eocService.currentVisitId();
    carePlanFileObj.patientId = this.patientService.patientIDsignal();

    const file = carePlanFileObj?.file as File;
    if (!file) {
      this.swalToastService.toastError('Please select a file.');
      return EMPTY;
    }

    return from(validateUpload(file)).pipe(
      mergeMap((res) => {
        if (!res.ok) {
          this.swalToastService.toastError(res.error!);
          return EMPTY;
        }

        const formData = new FormData();
        formData.append('Date', carePlanFileObj?.date!);
        formData.append('Description', carePlanFileObj?.description!);
        formData.append('file', file);
        formData.append('PatientId', carePlanFileObj?.patientId?.toString()!);
        formData.append('VisitId', carePlanFileObj?.visitId?.toString()!);

        return this._http.post(
          `${environment.BACKEND_URL}CarePlanFile`,
          formData
        );
      }),
      catchError((err) => {
        this.swalToastService.toastError(
          'Unable to submit the Care Plan file.'
        );
        throw err;
      })
    );
  }

  editCarePlanFile(carePlanFileObj: IAddCarePlanFile) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.put(
      `${environment.BACKEND_URL}CarePlanFile/${carePlanFileObj.id}`,
      carePlanFileObj,
      {
        headers: headers,
      }
    );
  }

  deleteCarePlanFile(carePlanFileId: string): Observable<IServiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.delete<IServiceResponse>(
      `${environment.BACKEND_URL}CarePlanFile/${carePlanFileId}`,
      {
        headers: headers,
      }
    );
  }

  deleteCarePlanFileFromTable(carePlanFileId: string) {
    // REMOVE THE Laboratory
    this.carePlanFileDataSub.next(
      this.carePlanFileDataSub.value?.filter(
        (rp) => rp.id?.toString() != carePlanFileId
      )
    );
  }
}
